import { useMemo } from 'react';
import { useCredits } from '@/hooks/useCredits';
import { useAuth } from '@/hooks/useAuth';

export interface MembershipStatus {
  planName: string;
  planId: string | null;
  isFree: boolean;
  isPaid: boolean;
  hasActiveSubscription: boolean;
  canAccessTools: boolean;
  monthlyCredits: number;
  isLoading: boolean;
}

/**
 * Hook to check user's membership/subscription status
 * 
 * A user can access tools if:
 * - They have a paid plan (not free)
 * - OR they have purchased a credit pack (lifetime_earned > monthly_credits of free plan)
 */
export function useMembership(): MembershipStatus {
  const { user } = useAuth();
  const { plan, isLoading, balance } = useCredits();

  return useMemo(() => {
    if (!user) {
      return {
        planName: 'Not logged in',
        planId: null,
        isFree: true,
        isPaid: false,
        hasActiveSubscription: false,
        canAccessTools: false,
        monthlyCredits: 0,
        isLoading,
      };
    }

    const isFree = plan?.is_free ?? true;
    const isPaid = !isFree;
    const hasActiveSubscription = isPaid;
    
    // Users can access tools if:
    // 1. They have a paid subscription, OR
    // 2. They have credits (even on free plan with purchased credits)
    const canAccessTools = isPaid || balance > 0;

    return {
      planName: plan?.name || 'Free',
      planId: plan?.id || null,
      isFree,
      isPaid,
      hasActiveSubscription,
      canAccessTools,
      monthlyCredits: plan?.monthly_credits || 0,
      isLoading,
    };
  }, [user, plan, isLoading, balance]);
}

export default useMembership;
